import React from "react";
import { styled } from "@mui/material";
import { FirmWithLenses, NamedUser } from "generated/graphql";
import { StateHandle } from "StateHandle";
import { FormState, schemas, useForm } from "helpers/FormHelpers";
import { ALL_STATE_CODES, StateCode } from "helpers/StateCode";
import UserAutoComplete from "./UserAutocomplete";
import PDatePicker from "./PDatePicker";
import { FormSelectField, FormTextField } from "helpers/FormHelpersComponents";
import { CaseInfoUI } from "helpers/caseInfo";
import { FormControl, InputLabel, MenuItem } from "@mui/material";
import PSelect from "components/PSelect";
import { useShowSBR } from "SBR/useShowSBR";

const RootContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gridGap: 16,
});

const initial: (caseInfo: CaseInfoUI) => FormState<CaseInfoField> = (caseInfo: CaseInfoUI) => ({
  values: {
    clientCaseId: caseInfo.clientCaseId,
    coCounsel: caseInfo.coCounsel || "",
    claimantGivenName: caseInfo.claimantGivenName,
    claimantMiddleName: caseInfo.claimantMiddleName || "",
    claimantSurname: caseInfo.claimantSurname || "",
    dateOfBirth: caseInfo.dateOfBirth,
    dateOfDeath: caseInfo.dateOfDeath,
    stateOfResidence: caseInfo.stateOfResidence || "",
  },
  isValid: false,
  touched: {
    clientCaseId: false,
    coCounsel: false,
    claimantGivenName: false,
    claimantMiddleName: false,
    claimantSurname: false,
    dateOfBirth: false,
    dateOfDeath: false,
    assignee: false,
    stateOfResidence: false,
  },
  errors: {},
});

type CaseInfoField =
  | "clientCaseId"
  | "claimantGivenName"
  | "claimantMiddleName"
  | "claimantSurname"
  | "dateOfBirth"
  | "dateOfDeath"
  | "coCounsel"
  | "stateOfResidence";

const schema = {
  clientCaseId: schemas.ident("Case ID"),
  coCounsel: schemas.basicOptional(),
  claimantGivenName: schemas.nonEmpty("First Name"),
  claimantMiddleName: schemas.basicOptional(),
  claimantSurname: schemas.nonEmpty("Last Name"),
  dateOfBirth: schemas.customType(false),
  dateOfDeath: schemas.customType(false),
  assignee: schemas.basicOptional(),
  stateOfResidence: {},
};

export interface CaseInfoFormProps {
  editable: boolean;
  caseInfoHandle: StateHandle<CaseInfoUI>;
  assigneeHandle: StateHandle<NamedUser | undefined>;
  firmHandle?: StateHandle<FirmWithLenses | undefined>;
  validHandle: StateHandle<boolean>;
  caseIdError?: string;
  editableId: boolean;
  assigneeOptions?: NamedUser[];
  firms?: FirmWithLenses[];
}

const CaseInfoForm = (props: CaseInfoFormProps) => {
  const { caseInfoHandle, validHandle, caseIdError, editableId, assigneeHandle, firmHandle, firms } = props;
  const [assignee, setAssignee] = assigneeHandle;
  const sbr = useShowSBR();

  const form = useForm<CaseInfoField>(initial(props.caseInfoHandle[0]), schema, [
    { name: "dateOfBirth" },
    { name: "dateOfDeath" },
  ]);

  const formState = form.formStateHandle[0];
  const values = formState.values;

  function handleFirmChange(evt) {
    if (firmHandle) {
      const firm = (firms || []).find((firm) => firm.id === evt.target.value);
      firmHandle[1](firm);
    }
  }

  React.useEffect(() => {
    if (caseIdError) {
      const err = caseIdError;
      form.formStateHandle[1]((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          clientCaseId: [err],
        },
      }));
    }
  }, [caseIdError]);

  React.useEffect(() => {
    const [, setValid] = validHandle;
    setValid(formState.isValid);
  }, [formState.isValid]);

  React.useEffect(() => {
    const caseInfo: CaseInfoUI = {
      clientCaseId: (values.clientCaseId as string).trim(),
      coCounsel: values.coCounsel as string | undefined,
      claimantGivenName: (values.claimantGivenName as string).trim(),
      claimantMiddleName: values.claimantMiddleName as string | undefined,
      claimantSurname: (values.claimantSurname as string).trim(),
      dateOfBirth: values.dateOfBirth,
      dateOfDeath: values.dateOfDeath,
      stateOfResidence: (values.stateOfResidence as StateCode) || undefined,
    };
    caseInfoHandle[1](caseInfo);
  }, [
    values.clientCaseId,
    values.coCounsel,
    values.claimantGivenName,
    values.claimantMiddleName,
    values.claimantSurname,
    values.coCounsel,
    values.dateOfBirth,
    values.dateOfDeath,
    values.stateOfResidence,
  ]);

  return (
    <RootContainer>
      <FormTextField<CaseInfoField>
        fullWidth
        size="small"
        label="Case ID"
        id="clientCaseId"
        required
        useForm={form}
        disabled={!editableId}
      />
      {firms && firms.length > 0 && firmHandle && (
        <FormControl size="small" fullWidth>
          <InputLabel variant="outlined">Select Firm</InputLabel>
          <PSelect label="Select Firm" value={firmHandle[0] ? firmHandle[0].id : ""} onChange={handleFirmChange}>
            <MenuItem key="none" value="">
              No Firm
            </MenuItem>

            {firms.map((firm) => (
              <MenuItem key={firm.id} value={firm.id}>
                {firm.name}
              </MenuItem>
            ))}
          </PSelect>
        </FormControl>
      )}
      <FormTextField<CaseInfoField>
        fullWidth
        size="small"
        label="Co-Counsel"
        id="coCounsel"
        useForm={form}
        disabled={!props.editable}
      />
      {!sbr && (
        <UserAutoComplete
          label="Assignee"
          options={props.assigneeOptions || []}
          selected={assignee}
          onChange={setAssignee}
        />
      )}
      <FormTextField<CaseInfoField>
        fullWidth
        size="small"
        label="First Name"
        id="claimantGivenName"
        required
        useForm={form}
        disabled={!props.editable}
      />
      <FormTextField<CaseInfoField>
        fullWidth
        size="small"
        label="Last Name"
        id="claimantSurname"
        required
        useForm={form}
        disabled={!props.editable}
      />
      <FormTextField<CaseInfoField>
        fullWidth
        size="small"
        label="Middle Name"
        id="claimantMiddleName"
        useForm={form}
        disabled={!props.editable}
      />
      <FormSelectField<CaseInfoField, StateCode>
        fullWidth
        label="State"
        id="stateOfResidence"
        useForm={form}
        options={ALL_STATE_CODES}
        disabled={!props.editable}
      />
      <PDatePicker
        label="Date of Birth"
        date={values.dateOfBirth}
        setDate={form.updateField("dateOfBirth")}
        error={form.errorText("dateOfBirth")}
        setError={(e) => form.setFieldError("dateOfBirth")(e !== undefined ? [e] : undefined)}
        disabled={!props.editable}
      />
      <PDatePicker
        label="Date of Death"
        date={values.dateOfDeath}
        setDate={form.updateField("dateOfDeath")}
        error={form.errorText("dateOfDeath")}
        setError={(e) => form.setFieldError("dateOfDeath")(e !== undefined ? [e] : undefined)}
        disabled={!props.editable}
      />
    </RootContainer>
  );
};

export default CaseInfoForm;
